import z from "zod";
import { OrderStatusEnum, OrderTypeEnum } from "../domain/entities/OrdersEntity";
import { OrdersApiRepository } from "./OrdersApiRepository";
import { Badge } from "@/components/atoms/Badge";

export const OrderTypeOptions = [
    { label: OrderTypeEnum[OrderTypeEnum.COMPRA], value: OrderTypeEnum.COMPRA.toString() },
    { label: OrderTypeEnum[OrderTypeEnum.VENTA], value: OrderTypeEnum.VENTA.toString() },
];

export const OrderStatusOptions = [
    { label: OrderStatusEnum[OrderStatusEnum.REGISTRO], value: OrderStatusEnum.REGISTRO.toString() },
    { label: OrderStatusEnum[OrderStatusEnum.COMPLEMENTACION], value: OrderStatusEnum.COMPLEMENTACION.toString() },
    { label: OrderStatusEnum[OrderStatusEnum.FIN], value: OrderStatusEnum.FIN.toString() },
];

export const variantMap: Record<OrderStatusEnum, "info" | "warning" | "success"> = {
    [OrderStatusEnum.REGISTRO]: "info",
    [OrderStatusEnum.COMPLEMENTACION]: "warning",
    [OrderStatusEnum.FIN]: "success",
};

export const columns = [
    {
        header: "Estatus",
        accessor: "status",
        render: (value: string) => {
            const variant = variantMap[value as unknown as OrderStatusEnum] ?? "info";
            return <Badge variant={variant}>{value.charAt(0)}</Badge>;
        },
        filterValue: (value: string) => value
    },
    { header: "Portafolio", accessor: "portfolio.name" },
    {
        header: "Tipo",
        accessor: "orderType",
        render: (value: string) => {
            const variant = value === OrderTypeEnum.COMPRA ? "darkInfo" : "darkError";
            return (
                <Badge variant={variant} className="w-full">
                    {`${value}`}
                </Badge>
            );
        }
    },
    { header: "Instrumento", accessor: "instrument.mnemonic" },
    {
        header: "Cantidad",
        accessor: "quantity",
        render: (value: number) =>
            new Intl.NumberFormat("es-ES", {
                style: "decimal",
                minimumFractionDigits: 2,
            }).format(value),
    },
    {
        header: "Tasa",
        accessor: "rate",
        render: (value: number) =>
            new Intl.NumberFormat("es-ES", {
                style: "percent",
                minimumFractionDigits: 2,
            }).format(value / 100),
    },
    {
        header: "Valor",
        accessor: "value",
        render: (value: number) =>
            `$${value.toLocaleString("es-CO",
                { minimumFractionDigits: 2, maximumFractionDigits: 2 }
            )}`
    },
    {
        header: "Creado",
        accessor: "createdAt",
        render: (value: Date) => value.toDateString(),
    },
];

export const orderTypeOptions = [
    { label: OrderTypeEnum[OrderTypeEnum.COMPRA], value: OrderTypeEnum.COMPRA.toString() },
    { label: OrderTypeEnum[OrderTypeEnum.VENTA], value: OrderTypeEnum.VENTA.toString() },
];

export const schema = z.object({
    id: z.string().optional(),
    portfolio: z.string().min(1, "El portafolio es obligatorio"),
    orderType: z.boolean(),
    instrument: z.string().min(1, "El instrumento es obligatorio"),
    quantity: z.number({ invalid_type_error: "La cantidad debe ser un número" })
        .min(1, "Debe ser mayor a 0"),
    rate: z.number({ invalid_type_error: "La tasa debe ser un número" })
        .min(1, "Debe ser mayor a 0")
        .max(100, "Debe ser menor o igual a 100"),
    value: z.number({ invalid_type_error: "El valor debe ser un número" })
        .min(1, "Debe ser mayor a 0"),
    confirmNumber: z.string().min(1, "El portafolio es obligatorio").optional(),
    status: z.string().optional()
});

export type OrderFormData = z.infer<typeof schema>;

export const orderRepository = new OrdersApiRepository();

export const orderKeys = {
    all: ['orders'] as const,
    details: (id: string) => [...orderKeys.all, id] as const,
    allTypes: ['orderTypes'] as const,
    allPortfolios: ['portfolio'] as const,
    allInstruments: ['instrument'] as const,
    allRates: ['rate'] as const,
};